import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ProgressBar, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faBook, faSpinner } from '@fortawesome/free-solid-svg-icons';
import styles from '../styles/RecipeBookExporter.module.css';
import { exportRecipeBook } from '../services/recipePdfService';

function RecipeBookExporter({ show, handleClose }) {
  const [courses, setCourses] = useState({
    Appetizer: true,
    Breakfast: true,
    Lunch: true,
    Dinner: true,
    Entree: true,
    Dessert: true,
    Snack: true,
    Other: true
  });
  const [includeImages, setIncludeImages] = useState(true);
  const [onlyUserRecipes, setOnlyUserRecipes] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('');
  const [error, setError] = useState(null);

  const handleCourseChange = (course) => {
    setCourses(prev => ({
      ...prev,
      [course]: !prev[course]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(courses).every(v => v);
    const newValue = !allSelected;
    setCourses(Object.keys(courses).reduce((acc, course) => {
      acc[course] = newValue;
      return acc;
    }, {}));
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportStatus('Fetching recipes...');
    setError(null);

    try {
      // Get selected courses
      const selectedCourses = Object.entries(courses)
        .filter(([_, selected]) => selected)
        .map(([course, _]) => course);

      if (selectedCourses.length === 0) {
        throw new Error('Please select at least one course to export');
      }

      // Fetch recipes for selected courses
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/export`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          courses: selectedCourses,
          onlyUserRecipes: onlyUserRecipes 
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recipes');
      }

      const recipes = await response.json();
      
      if (recipes.length === 0) {
        throw new Error('No recipes found for the selected courses');
      }

      // Generate PDF
      await exportRecipeBook(
        recipes, 
        includeImages,
        (progress, status) => {
          setExportProgress(progress);
          setExportStatus(status);
        }
      );

      setExportStatus('Export complete!');
      setTimeout(() => {
        handleClose();
        setIsExporting(false);
        setExportProgress(0);
        setExportStatus('');
      }, 2000);

    } catch (err) {
      setError(err.message);
      setIsExporting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faBook} /> Export Recipe Book
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.exportOptions}>
          <div className={styles.courseSelection}>
            <h5>Select Courses to Include:</h5>
            <Button 
              variant="link" 
              size="sm" 
              onClick={handleSelectAll}
              className={styles.selectAllBtn}
            >
              {Object.values(courses).every(v => v) ? 'Deselect All' : 'Select All'}
            </Button>
            <div className={styles.courseGrid}>
              {Object.entries(courses).map(([course, selected]) => (
                <Form.Check
                  key={course}
                  type="checkbox"
                  id={`course-${course}`}
                  label={course}
                  checked={selected}
                  onChange={() => handleCourseChange(course)}
                  disabled={isExporting}
                />
              ))}
            </div>
          </div>

          <div className={styles.additionalOptions}>
            <Form.Check
              type="checkbox"
              id="include-images"
              label="Include recipe images (larger file size)"
              checked={includeImages}
              onChange={(e) => setIncludeImages(e.target.checked)}
              disabled={isExporting}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="only-user-recipes"
              label="My recipes only (exclude default recipes)"
              checked={onlyUserRecipes}
              onChange={(e) => setOnlyUserRecipes(e.target.checked)}
              disabled={isExporting}
            />
          </div>

          {isExporting && (
            <div className={styles.progressSection}>
              <ProgressBar 
                now={exportProgress} 
                label={`${exportProgress}%`}
                animated
                striped
              />
              <p className={styles.statusText}>
                <FontAwesomeIcon icon={faSpinner} spin /> {exportStatus}
              </p>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleExport}
          disabled={isExporting || Object.values(courses).every(v => !v)}
        >
          <FontAwesomeIcon icon={faDownload} /> Export to PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RecipeBookExporter;